/**
 * Nimiketyypit tulevat kannasta (Tekninen ylläpito hallitsee niitä), ja niihin viitataan id:llä (nimikkeen typeId).
 * Toiminnot eivät viittaa yksittäisiin tyyppeihin vaan tyypin käyttötarkoitukseen (purpose) ja ominaisuuksiin.
 *
 * Tyypit ladataan storeen kerran per sivulataus: kutsu `await loadItemTypes()` ennen tyyppiperusteista nimikehakua.
 * Funktiot lukevat storen tilaa, joten computed-arvot päivittyvät, kun tyypit latautuvat.
 * Valintalistat käyttävät aktiivisia tyyppejä (activeTypeIds), luokittelu ja raportit kaikkia (typeIds).
 */
import store from '@/store';

export const PURPOSE = {
  MATERIAL: 'MATERIAL',
  WORK_TASK: 'WORK_TASK',
  ABSENCE: 'ABSENCE',
  LICENSE: 'LICENSE',
  EQUIPMENT_TYPE: 'EQUIPMENT_TYPE',
  DRILLING_METHOD: 'DRILLING_METHOD',
  REAMING_SIZE: 'REAMING_SIZE',
};

// Kirjanpidollinen luokka tekstiavaimena (ALIT-625); valitaan kaikille nimiketyypeille.
export const ACCOUNTING_CLASS = {
  VAIHTOOMAISUUS: 'vaihtoomaisuus',
  KAYTTOOMAISUUS: 'kayttoomaisuus',
  MUU: 'muu',
};

// Materiaalin ominaisuudet (tyypin liput); hallinta näyttää ne tässä järjestyksessä.
export const FEATURES = ['trackInventory', 'showUnit', 'showWarehouseUnit', 'pipe', 'siteAdditionalItem'];

export const loadItemTypes = (force = false) => store.dispatch('loadItemTypes', { force });

// Järjestyksessä (sort_order), myös käytöstä poistetut
export const allItemTypes = () => store.state.itemTypes;

export const itemType = id => (id == null ? null : allItemTypes().find(t => t.id === id) || null);

export const withPurpose = purpose => type => type.purpose === purpose;

export const typeIds = predicate => allItemTypes().filter(predicate).map(t => t.id);

export const activeTypeIds = predicate => allItemTypes().filter(t => t.active && predicate(t)).map(t => t.id);

// Tuntematon tyyppi viimeisenä
export const typeOrder = id => {
  const index = allItemTypes().findIndex(t => t.id === id);
  return index < 0 ? allItemTypes().length : index;
};

export const typeName = id => itemType(id)?.name || '';

export const typePluralName = id => itemType(id)?.pluralName || '';

export const hasPurpose = (id, purpose) => itemType(id)?.purpose === purpose;

export const isTrackable = id => itemType(id)?.trackInventory ?? false;

export const hasUnit = id => itemType(id)?.showUnit ?? false;

export const hasWarehouseUnit = id => itemType(id)?.showWarehouseUnit ?? false;

export const isPipeType = id => itemType(id)?.pipe ?? false;

export const isSiteAdditionalItem = id => itemType(id)?.siteAdditionalItem ?? false;

// Työtehtävä- ja poissaolonimikkeitä ei muokata luonnin jälkeen: palkka-aineisto tunnistaa ne nimestä (ALIT-401).
export const isEditable = id => !hasPurpose(id, PURPOSE.WORK_TASK) && !hasPurpose(id, PURPOSE.ABSENCE);

// ===== Menetelmät ilman putkea (menetelmänimikkeen nimen mukaan) =====
export const NO_PIPE_METHODS = ['koeporaus', 'avarrus'];
export const isNoPipeMethod = methodName => NO_PIPE_METHODS.includes(methodName?.toLowerCase());
